import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prospectId, setProspectId] = useState<string | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError('Invalid verification link. Missing verification token.');
        setVerifying(false);
        return;
      }

      try {
        const apiUrl = import.meta.env.DEV
          ? `http://localhost:8000/v1/platform/api/enterprise/prospect-workflow/verify-email/`
          : `/v1/platform/api/enterprise/prospect-workflow/verify-email/`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setSuccess(true);
          setProspectId(data.data.prospect_id);
        } else {
          setError(data.error || 'Verification failed');
        }
      } catch {
        setError('Network error occurred while verifying email');
      } finally {
        setVerifying(false);
      }
    };

    const timer = setTimeout(() => {
      verifyEmail();
    }, 1000);

    return () => clearTimeout(timer);
  }, [token]);

  if (verifying) {
    return (
      <div className="flex justify-center items-center h-screen bg-background">
        <Card className="min-w-[400px] text-center">
          <CardContent className="pt-8 pb-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <h2 className="text-xl font-semibold mt-5">Verifying your email...</h2>
            <p className="text-muted-foreground mt-2">Please wait while we verify your email address.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex justify-center items-center h-screen bg-background">
        <Card className="max-w-lg w-full text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <CheckCircle className="h-16 w-16 text-success-700 mx-auto" />
            <h2 className="text-2xl font-semibold">Email Verified Successfully!</h2>
            <p className="text-muted-foreground">
              Your email has been verified. You can now proceed with payment to activate your LabBoxx account.
            </p>
            <div className="flex gap-3 justify-center pt-2">
              <Button
                onClick={() => {
                  if (prospectId) {
                    navigate(`/track-order/${prospectId}`);
                  } else {
                    navigate('/');
                  }
                }}
              >
                Continue to Tracking Page
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}>
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center h-screen bg-background">
      <Card className="max-w-lg w-full text-center">
        <CardContent className="pt-8 pb-8 space-y-4">
          <AlertCircle className="h-16 w-16 text-destructive mx-auto" />
          <h2 className="text-2xl font-semibold">Email Verification Failed</h2>
          <p className="text-muted-foreground">{error || 'Unable to verify your email address.'}</p>
          <div className="flex gap-3 justify-center pt-2">
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
            <Button variant="outline" onClick={() => window.open('mailto:support@labboxx.com')}>
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailVerification;
